<script setup lang="ts">
import type { Schemas } from '#shopware'
import { pascalCase } from 'scule'
import { useExperiences } from '#imports'

const props = defineProps<{
    content: Schemas['CmsElementSliderBase']
}>()

const sliderContentType = computed(() => props.content?.config?.sliderContent?.value ?? '')

const isMultiExperience = computed(() => sliderContentType.value === 'multi-travel-card-slider')
const isSingleExperience = computed(() => sliderContentType.value === 'single-travel-card-slider')
const isExperienceType = computed(() => isMultiExperience.value || isSingleExperience.value)

const experienceTitle = computed(() => props.content?.config?.experiences?.value?.title ?? '')

const slidesPerView = computed(() => {
    const config = {
        xs: props.content?.config?.slidesPerViewXs?.value,
        sm: props.content?.config?.slidesPerViewSm?.value,
        md: props.content?.config?.slidesPerViewMd?.value,
        lg: props.content?.config?.slidesPerViewLg?.value,
        xl: props.content?.config?.slidesPerViewLg?.value,
        '2xl': props.content?.config?.slidesPerViewLg?.value,
    }

    const withDefaults = (defaults: Record<string, number>) => ({
        xs: config.xs ?? defaults.xs,
        sm: config.sm ?? defaults.sm,
        md: config.md ?? defaults.md,
        lg: config.lg ?? defaults.lg,
        xl: config.xl ?? defaults.xl,
        '2xl': config['2xl'] ?? defaults['2xl'],
    })

    if (isSingleExperience.value) return withDefaults({ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, '2xl': 1 })
    if (isMultiExperience.value) return withDefaults({ xs: 1.1, sm: 2.1, md: 2, lg: 3.3, xl: 3.3, '2xl': 3.3 })
    return config
})

const spaceBetween = computed(() => {
    const configValue = props.content.config?.spaceBetween?.value
    if (isSingleExperience.value) return configValue ?? 0
    if (isMultiExperience.value) return configValue ?? 24
    return configValue
})

// Experience data: prefer resolver data (data.listing), fallback to client-side fetch
const experienceIds = computed(() => props.content?.config?.experiences?.value?.slides ?? [])
const { experiences: experiencesFallback, fetchExperiences } = useExperiences()

const items = computed(() => {
    if (isExperienceType.value) {
        const listing = props.content?.data?.listing
        if (listing?.length) return listing
        return experiencesFallback.value
    }
    return Object.values(props.content?.data?.elements ?? {})
})

// Client-side fallback: fetch experiences if resolver data not available
if (isExperienceType.value && experienceIds.value.length) {
    const hasResolverData = (props.content?.data?.listing?.length ?? 0) > 0
    if (!hasResolverData) {
        const key = `experiences-${experienceIds.value.join('-')}-${sliderContentType.value}`
        const { data: asyncData } = await useAsyncData(key, () => fetchExperiences({ ids: experienceIds.value }))
        if (asyncData.value) experiencesFallback.value = asyncData.value.elements
    }
}

const slideComponent = resolveComponent(
    `SliderSlideContent${pascalCase(sliderContentType.value)}`
)

if (props.content?.config?.promotion?.value) {
    provide('trackingData', { promotion: props.content?.config.promotion.value })
}

provide('sliderConfig', props.content?.config)
</script>

<template>
    <!-- Multi Experience: Headline-Tile left + Carousel right -->
    <div
        v-if="isMultiExperience && items.length"
        class="flex flex-col lg:flex-row gap-4 md:gap-6 px-2 py-8 md:px-6 md:py-12 lg:px-0 lg:py-0 lg:pt-16"
    >
        <div class="lg:w-[280px] shrink-0 flex flex-col gap-4 md:gap-6 lg:gap-0 lg:justify-between">
            <h2
                v-if="experienceTitle"
                class="font-semibold text-4xl leading-[125%]"
            >
                {{ experienceTitle }}
            </h2>
            <!-- Teleport target — navigation will appear here on desktop -->
            <div id="multi-experience-nav" />
        </div>
        <div class="flex-1 min-w-0 -mr-4 md:-mr-6 lg:mr-0">
            <SliderBase
                :slides-per-view="slidesPerView"
                :slide-count="items.length"
                :mobile-grid="content?.config?.gridDisplay?.value"
                :display-nav="true"
                :space-between="spaceBetween"
                :progress-config="content?.config?.progressConfig?.value"
                teleport-target="#multi-experience-nav"
            >
                <template #default="{ index }">
                    <div
                        v-if="items[index] != null"
                        class="product-swiper-slide w-full h-full"
                    >
                        <component
                            :is="slideComponent"
                            :config="items[index]"
                            :index="index"
                        />
                    </div>
                </template>
            </SliderBase>
        </div>
    </div>

    <!-- All other slider types -->
    <SliderBase
        v-else
        :slides-per-view="slidesPerView"
        :slide-count="items.length"
        :mobile-grid="content?.config?.gridDisplay?.value"
        :display-nav="isSingleExperience ? true : content?.config?.displayNav?.value"
        :space-between="spaceBetween"
        :progress-config="content?.config?.progressConfig?.value"
        :nav-position="isSingleExperience ? 'below' : 'above'"
    >
        <template #default="{ index }">
            <div
                v-if="items[index] != null"
                class="product-swiper-slide w-full"
                :class="{ 'h-full': isSingleExperience }"
            >
                <component
                    :is="slideComponent"
                    :config="items[index]"
                    :index="index"
                />
            </div>
        </template>
    </SliderBase>
</template>
